import { useRef, useState, type DragEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import UploadFile from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import type { OnlineImportAnalysis, OnlineImportFormat, OnlineImportTarget } from './onlineImportService';
import {
  columnMatches,
  fileColumns,
  headerReadAsData,
  isWorkbook,
  type OnlineImportEvent,
  type OnlineImportState,
} from './onlineImportState';
import { OnlineImportColumns } from './OnlineImportColumns';

const ACCEPT = '.csv,.tsv,.txt,.xlsx';
const ENCODINGS = ['utf-8', 'windows-1252'];

/** The rows of the file shown as they are read. */
const RAW_ROWS = 3;

interface OnlineImportFileStepProps {
  state: OnlineImportState;
  target: OnlineImportTarget | undefined;
  dispatch: (event: OnlineImportEvent) => void;
  /** The file is being read. */
  reading: boolean;
  columnsOpen: boolean;
  onColumnsOpenChange: (open: boolean) => void;
}

/**
 * Step 1: the file — read as soon as it is chosen, and again when how to read it changes — its
 * first rows as read, whether the columns the mapping reads are there, and the columns themselves,
 * adjustable for this run.
 */
export function OnlineImportFileStep({ state, target, dispatch, reading, columnsOpen, onColumnsOpenChange }: OnlineImportFileStepProps) {
  const change = (format: Partial<OnlineImportFormat>) => dispatch({ type: 'formatChanged', format });
  if (!state.file) {
    return <DropZone maxRows={target?.maxRows} onFile={(file) => dispatch({ type: 'fileChosen', file })} />;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <FileChip file={state.file} analysis={state.analysis} maxRows={target?.maxRows} reading={reading} onChange={() => dispatch({ type: 'fileRemoved' })} />
      {isWorkbook(state.file)
        ? <WorkbookSettings state={state} onChange={change} />
        : <CsvSettings format={state.format} onChange={change} />}
      {state.analysis && (
        <>
          <RawRows analysis={state.analysis} firstRowIsData={!state.format.hasHeader && !isWorkbook(state.file)} />
          {state.preset && <ColumnCheck state={state} onUseHeader={() => change({ hasHeader: true })} />}
          {target && (
            <OnlineImportColumns
              state={state}
              target={target}
              dispatch={dispatch}
              open={columnsOpen}
              onOpenChange={onColumnsOpenChange}
            />
          )}
        </>
      )}
    </Box>
  );
}

function DropZone({ maxRows, onFile }: { maxRows?: number; onFile: (file: File) => void }) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const drop = (event: DragEvent) => {
    event.preventDefault();
    setOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) onFile(file);
  };
  return (
    <Box
      onDragOver={(event) => { event.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      sx={{
        border: '1.5px dashed', borderColor: over ? 'primary.main' : 'divider', borderRadius: 1,
        bgcolor: over ? 'action.hover' : 'transparent',
        px: 2, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center',
      }}
    >
      <Button variant="outlined" startIcon={<UploadFile />} onClick={() => input.current?.click()} sx={{ textTransform: 'none' }}>
        {t('onlineImport.chooseFile', { defaultValue: 'Choose file' })}
      </Button>
      <Typography variant="body2" color="text.secondary">
        {t('onlineImport.dropHint', { defaultValue: 'or drag a file here' })}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {maxRows === undefined
          ? t('onlineImport.fileTypes', { defaultValue: 'CSV or Excel (.xlsx)' })
          : t('onlineImport.fileHint', {
            defaultValue: 'CSV or Excel (.xlsx) · up to {{count}} rows. Larger files are imported through an integration job.',
            count: maxRows,
          })}
      </Typography>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        hidden
        data-testid="online-import-file"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onFile(file);
          event.target.value = '';
        }}
      />
    </Box>
  );
}

interface FileChipProps {
  file: File;
  analysis: OnlineImportAnalysis | null;
  maxRows?: number;
  reading: boolean;
  onChange: () => void;
}

function FileChip({ file, analysis, maxRows, reading, onChange }: FileChipProps) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 1, px: 1.25, py: 0.5, bgcolor: 'action.hover' }}>
        <InsertDriveFileOutlined fontSize="small" color="action" />
        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.name}
          {analysis && <Box component="span" sx={{ color: 'text.secondary' }}> · {fileShape(analysis, maxRows, t)}</Box>}
          {reading && <Box component="span" sx={{ color: 'text.secondary' }}> · {t('onlineImport.reading', { defaultValue: 'Reading the file…' })}</Box>}
        </Typography>
        <Button size="small" onClick={onChange} sx={{ textTransform: 'none', minWidth: 0 }}>
          {t('onlineImport.changeFile', { defaultValue: 'Change' })}
        </Button>
      </Box>
      {reading && <LinearProgress />}
    </Box>
  );
}

function fileShape(analysis: OnlineImportAnalysis, maxRows: number | undefined, t: ReturnType<typeof useTranslation>['t']): string {
  const columns = fileColumns(analysis).length;
  return analysis.exceedsMaxRows && maxRows !== undefined
    ? t('onlineImport.fileShapeOverCap', { defaultValue: 'more than {{count}} rows, {{columns}} columns', count: maxRows, columns })
    : t('onlineImport.fileShape', { defaultValue: '{{count}} rows, {{columns}} columns', count: analysis.rowCount, columns });
}

function CsvSettings({ format, onChange }: { format: OnlineImportFormat; onChange: (format: Partial<OnlineImportFormat>) => void }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, rowGap: 1.5, flexWrap: 'wrap' }}>
      <FormControlLabel
        sx={{ mr: 'auto' }}
        control={<Checkbox size="small" checked={format.hasHeader} onChange={(e) => onChange({ hasHeader: e.target.checked })} />}
        label={<Typography variant="body2">{t('onlineImport.hasHeader', { defaultValue: 'First row is header' })}</Typography>}
      />
      <TextField
        size="small"
        label={t('onlineImport.delimiter', { defaultValue: 'Delimiter' })}
        placeholder={t('onlineImport.delimiterAuto', { defaultValue: 'auto' })}
        value={format.delimiter ?? ''}
        onChange={(e) => onChange({ delimiter: e.target.value.slice(0, 1) || null })}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ width: 100 }}
      />
      <TextField
        select
        size="small"
        label={t('onlineImport.encoding', { defaultValue: 'Encoding' })}
        value={format.encoding ?? 'utf-8'}
        onChange={(e) => onChange({ encoding: e.target.value })}
        sx={{ width: 150 }}
      >
        {ENCODINGS.map((encoding) => <MenuItem key={encoding} value={encoding}>{encoding}</MenuItem>)}
      </TextField>
    </Box>
  );
}

/** Row numbers are shown as a spreadsheet shows them (1 = the first row); the server counts from 0. */
function WorkbookSettings({ state, onChange }: { state: OnlineImportState; onChange: (format: Partial<OnlineImportFormat>) => void }) {
  const { t } = useTranslation();
  const toIndex = (value: string) => (value === '' ? null : Math.max(0, Number(value) - 1));
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {state.worksheets.length > 1 && (
        <TextField
          select
          size="small"
          label={t('onlineImport.worksheet', { defaultValue: 'Worksheet' })}
          value={state.format.worksheetName ?? ''}
          onChange={(e) => onChange({ worksheetName: e.target.value })}
          sx={{ minWidth: 160 }}
        >
          {state.worksheets.map((sheet) => <MenuItem key={sheet} value={sheet}>{sheet}</MenuItem>)}
        </TextField>
      )}
      <TextField
        size="small"
        type="number"
        label={t('onlineImport.headerRow', { defaultValue: 'Header row' })}
        value={state.format.headerRowIndex == null ? '' : state.format.headerRowIndex + 1}
        onChange={(e) => onChange({ headerRowIndex: toIndex(e.target.value) })}
        placeholder="1"
        slotProps={{ htmlInput: { min: 1 }, inputLabel: { shrink: true } }}
        sx={{ width: 110 }}
      />
      <TextField
        size="small"
        type="number"
        label={t('onlineImport.dataStartRow', { defaultValue: 'First data row' })}
        value={state.format.dataStartRowIndex == null ? '' : state.format.dataStartRowIndex + 1}
        onChange={(e) => onChange({ dataStartRowIndex: toIndex(e.target.value) })}
        slotProps={{ htmlInput: { min: 1 }, inputLabel: { shrink: true } }}
        sx={{ width: 130 }}
      />
    </Box>
  );
}

/**
 * The file's first rows as the server read them. A CSV read without a header has its first row as
 * data, under the names the server gives its columns — the names a mapping of such a file uses.
 */
function RawRows({ analysis, firstRowIsData }: { analysis: OnlineImportAnalysis; firstRowIsData: boolean }) {
  const { t } = useTranslation();
  const columns = fileColumns(analysis);
  const rows = analysis.sampleRows.slice(0, RAW_ROWS);
  if (columns.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap', py: 0.5 } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              {columns.map((column) => <TableCell key={column} sx={{ color: 'text.secondary' }}>{column}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index} sx={firstRowIsData && index === 0 ? { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.16), '& td': { fontWeight: 600 } } : undefined}>
                {columns.map((column) => <TableCell key={column}>{row[column] ?? ''}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {firstRowIsData && (
        <Typography variant="caption" color="text.secondary">
          {t('onlineImport.raw.noHeader', {
            defaultValue: 'Without a header row the columns are named Column0, Column1, … and row 1 is read as data.',
          })}
        </Typography>
      )}
    </Box>
  );
}

/** Whether the columns the profile's mapping reads are in the file. */
function ColumnCheck({ state, onUseHeader }: { state: OnlineImportState; onUseHeader: () => void }) {
  const { t } = useTranslation();
  const matches = columnMatches(state.analysis!, state.mappings);
  if (matches.length === 0) return null;
  const missing = matches.filter((match) => !match.found).length;
  const headerAsData = headerReadAsData(state);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {missing === 0 ? (
        <Alert severity="success" sx={{ py: 0 }}>
          {t('onlineImport.match.allFound', { defaultValue: 'All {{count}} profile columns were found in the file.', count: matches.length })}
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ py: 0 }}>
          {t('onlineImport.match.missing', {
            defaultValue: '{{missing}} of {{count}} profile columns were not found in the file.', missing, count: matches.length,
          })}
          {headerAsData.length > 0 && (
            <>
              {' '}
              {t('onlineImport.match.headerReadAsData', {
                defaultValue: 'Row 1 holds {{columns}} but is read as data.', columns: headerAsData.join(', '),
              })}
              <Box sx={{ mt: 0.5 }}>
                <Button color="inherit" size="small" variant="outlined" onClick={onUseHeader} sx={{ textTransform: 'none' }}>
                  {t('onlineImport.match.useHeader', { defaultValue: 'Use row 1 as header' })}
                </Button>
              </Box>
            </>
          )}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {matches.map((match) => (
          <Chip
            key={match.sourceColumn}
            size="small"
            variant="outlined"
            color={match.found ? 'success' : 'warning'}
            icon={match.found ? <Check /> : <Close />}
            label={match.sourceColumn}
          />
        ))}
      </Box>
    </Box>
  );
}
